/**
 * Progress tracking for an async filesystem sort (#10846).
 *
 * The sort action enqueues a Celery task and calls `onSortStarted(taskId,
 * total)`. This hook then polls `GET /api/v2/tasks/sort/status`, refreshes the
 * tree as files move, and renders a persistent "Sorting X of N…" banner until
 * the task reaches a terminal state — so the operation visibly reports work
 * instead of the old "toast, then nothing, then the tree changes" behavior.
 *
 * The per-poll state transition lives in the pure `stepSortProgress` reducer so
 * the timer-driven hook stays thin and the tricky cases (Celery dropping the
 * PROGRESS meta on SUCCESS, repeated polling failures, no-advance polls) are
 * unit-testable without fake timers.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { CircleNotch, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import fpFetch from "@/services/fpFetch";
import { BASE_API_URL_V2 } from "@/constants";
import { invalidateFileTreeCache } from "./fetchFiles";
import {
  clearProductInterval,
  clearProductTimeout,
  setProductInterval,
  setProductTimeout,
  type ProductTimer,
} from "@/utils/productTimers";

interface SortStatusResponse {
  state?: string;
  done?: number;
  total?: number;
}

export interface SortProgressState {
  taskId: string;
  done: number;
  total: number;
  /** UI state: PENDING | PROGRESS | SUCCESS | FAILURE. */
  state: string;
  /** Consecutive failed polls; drives the fail-open error transition. */
  failureStreak: number;
}

/** Outcome of a single status poll, fed to the reducer. */
export type PollOutcome =
  | { ok: true; data: SortStatusResponse }
  | { ok: false };

export interface SortProgressStep {
  next: SortProgressState;
  /** Stop polling — the task reached a terminal state. */
  terminal: boolean;
  /** Refetch the tree — a file moved (progress advanced or the sort finished). */
  shouldRefresh: boolean;
}

const POLL_MS = 2000;
// Stop polling after ~30 min (900 * 2s) so a stuck task never polls forever.
const MAX_POLLS = 900;
// Flip the banner to an error after this many consecutive failed polls, rather
// than silently holding a stale count until MAX_POLLS (spec: show an error
// state, don't hang).
const MAX_CONSECUTIVE_POLL_FAILURES = 5;

/**
 * Compute the next banner state from the current state and one poll outcome.
 * Pure — no timers, no I/O — so it is directly unit-testable.
 */
export function stepSortProgress(
  prev: SortProgressState,
  outcome: PollOutcome
): SortProgressStep {
  if (!outcome.ok) {
    const failureStreak = prev.failureStreak + 1;
    if (failureStreak >= MAX_CONSECUTIVE_POLL_FAILURES) {
      // Give up: surface the failure instead of hanging on a stale count.
      return {
        next: { ...prev, state: "FAILURE", failureStreak },
        terminal: true,
        shouldRefresh: false,
      };
    }
    // Transient blip: keep the last known state, keep polling.
    return {
      next: { ...prev, failureStreak },
      terminal: false,
      shouldRefresh: false,
    };
  }

  const { data } = outcome;
  const state = data.state ?? "PENDING";
  const isSuccess = state === "SUCCESS";
  const isFailure = state === "FAILURE";
  // On completion Celery replaces the PROGRESS meta with the task's return
  // value, so done/total read 0 — treat SUCCESS as fully done.
  const reportedTotal = data.total || prev.total;
  const done = isSuccess ? reportedTotal : (data.done ?? prev.done);

  const next: SortProgressState = {
    taskId: prev.taskId,
    done,
    total: reportedTotal,
    state,
    failureStreak: 0,
  };

  return {
    next,
    terminal: isSuccess || isFailure,
    // Refetch only when a file actually moved or the sort finished — a poll
    // that reports the same count shouldn't churn the whole tree every 2s.
    shouldRefresh: isSuccess || isFailure || done !== prev.done,
  };
}

export function useSortProgress(refreshTree: () => void): {
  onSortStarted: (taskId: string, total: number) => void;
  sortBanner: React.ReactNode;
} {
  const [progress, setProgress] = useState<SortProgressState | null>(null);
  const intervalRef = useRef<ProductTimer | null>(null);
  const pollCountRef = useRef(0);
  // The reducer needs the latest state inside the interval closure without
  // re-arming the timer each render.
  const progressRef = useRef<SortProgressState | null>(null);
  progressRef.current = progress;
  // Holds the "leave the terminal state on screen briefly" timer, so it is
  // cleared on unmount instead of firing a setState on a gone component.
  const clearBannerRef = useRef<ProductTimer | null>(null);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearProductInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      stopPolling();
      clearProductTimeout(clearBannerRef.current);
    },
    [stopPolling]
  );

  const onSortStarted = useCallback(
    (taskId: string, total: number) => {
      stopPolling();
      clearProductTimeout(clearBannerRef.current);
      clearBannerRef.current = null;
      pollCountRef.current = 0;
      const initial: SortProgressState = {
        taskId,
        done: 0,
        total,
        state: "PENDING",
        failureStreak: 0,
      };
      progressRef.current = initial;
      setProgress(initial);

      intervalRef.current = setProductInterval(async () => {
        pollCountRef.current += 1;

        let outcome: PollOutcome = { ok: false };
        try {
          const resp = await fpFetch(
            `${BASE_API_URL_V2}/api/v2/tasks/sort/status?taskId=${encodeURIComponent(
              taskId
            )}`
          );
          if (resp.ok) {
            outcome = { ok: true, data: await resp.json() };
          }
        } catch {
          outcome = { ok: false };
        }

        const current = progressRef.current;
        if (!current) return;

        const { next, terminal, shouldRefresh } = stepSortProgress(
          current,
          outcome
        );
        progressRef.current = next;
        setProgress(next);

        if (shouldRefresh) {
          invalidateFileTreeCache();
          refreshTree();
        }

        if (terminal) {
          stopPolling();
          // Leave the terminal state on screen briefly, then clear.
          const isFailure = next.state === "FAILURE";
          clearProductTimeout(clearBannerRef.current);
          clearBannerRef.current = setProductTimeout(
            () => {
              clearBannerRef.current = null;
              setProgress(null);
            },
            isFailure ? 6000 : 2000
          );
          return;
        }

        if (pollCountRef.current > MAX_POLLS) {
          stopPolling();
          setProgress(null);
        }
      }, POLL_MS);
    },
    [refreshTree, stopPolling]
  );

  return {
    onSortStarted,
    sortBanner: <SortProgressBanner progress={progress} />,
  };
}

function SortProgressBanner({
  progress,
}: {
  progress: SortProgressState | null;
}): React.ReactNode {
  if (!progress) return null;

  const { done, total, state } = progress;
  const isSuccess = state === "SUCCESS";
  const isFailure = state === "FAILURE";
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;

  let icon: React.ReactNode;
  let text: string;
  if (isFailure) {
    icon = <WarningCircle size={16} weight="fill" className="text-red-600" />;
    text = "Sorting failed";
  } else if (isSuccess) {
    icon = <CheckCircle size={16} weight="fill" className="text-green-600" />;
    text =
      total > 0 ? `Sorted ${total} file${total === 1 ? "" : "s"}` : "Sorted";
  } else {
    icon = <CircleNotch size={16} className="animate-spin text-[#2A3DCB]" />;
    text =
      total > 0
        ? `Sorting ${done} of ${total} file${total === 1 ? "" : "s"}…`
        : "Sorting…";
  }

  return (
    <div
      className="flex items-center gap-2 px-3 py-2 mb-2 text-[13px] rounded border border-gray-200 bg-[#F7F8FF]"
      role="status"
      aria-live="polite"
    >
      {icon}
      <span className="text-gray-800">{text}</span>
      {!isSuccess && !isFailure && total > 0 && (
        <div className="flex-1 h-1.5 ml-2 bg-gray-200 rounded overflow-hidden">
          <div
            className="h-full bg-[#2A3DCB] transition-all duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
}
